import Button from "@cloudscape-design/components/button"
import Link from "@cloudscape-design/components/link"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import type { CapabilitySet } from "@/services/capabilitySetsApi"
import type { GrantItem, GrantTarget } from "@/services/grantsApi"
import GrantAccessModal from "@/components/Access/GrantAccessModal"
import GrantsTable, { type InheritedGrants } from "@/components/Access/GrantsTable"
import { useCapabilitySetsList } from "@/hooks/queries/useCapabilitySets"
import { useDtaGrantCandidates, useGrantsList } from "@/hooks/queries/useGrants"
import { isForbidden } from "@/utils/apiErrors"
import { sameEmail } from "@/utils/email"

interface DtaAccessPanelProps {
  dtaId: string
  studyId: string
  /**
   * DTA name for the grant modal title.
   */
  dtaName: string
  /**
   * Whether the DTA has a vendor to grant access to. EDC DTAs have none.
   */
  hasVendor: boolean
}

/**
 * DTA Collaboration tab access: the DTA's own grants (grant and revoke), then the inherited study
 * grants (read-only).
 */
export default function DtaAccessPanel({
  dtaId,
  studyId,
  dtaName,
  hasVendor,
}: DtaAccessPanelProps) {
  const navigate = useNavigate()
  const { sets, isPending: setsPending, error: setsError } = useCapabilitySetsList()
  const setsByName = useMemo(() => new Map(sets.map((s) => [s.name, s])), [sets])

  const dtaTarget: GrantTarget = useMemo(() => ({ type: "dta", id: dtaId }), [dtaId])
  const studyTarget: GrantTarget = useMemo(() => ({ type: "study", id: studyId }), [studyId])
  const dtaGrants = useGrantsList(dtaTarget)
  // Callers who cannot read the study get a 403 here and see no inherited rows.
  const studyGrants = useGrantsList(studyTarget)
  const [granting, setGranting] = useState(false)

  const studyAccessHref = `/studies/${studyId}?tab=access`
  const inherited: InheritedGrants = useMemo(
    () => ({ grants: studyGrants.grants, source: "Study", manageHref: studyAccessHref }),
    [studyGrants.grants, studyAccessHref],
  )
  const grantable = useMemo(
    () => sets.filter((s) => s.scope === "resource" && s.audience === "external"),
    [sets],
  )
  const studyLoadFailed = studyGrants.error !== null && !isForbidden(studyGrants.error)

  return (
    <>
      <GrantsTable
        target={dtaTarget}
        grants={dtaGrants.grants}
        loading={dtaGrants.isPending || studyGrants.isPending || setsPending}
        error={dtaGrants.error ?? setsError}
        setsByName={setsByName}
        title="Access"
        description={
          <>
            People with access to this DTA. Study access cascades here and is managed on the{" "}
            <Link
              href={studyAccessHref}
              onFollow={(e) => {
                e.preventDefault()
                navigate(studyAccessHref)
              }}
            >
              study&apos;s Access tab
            </Link>
            .
          </>
        }
        canManage={dtaGrants.canManage}
        actions={
          hasVendor &&
          dtaGrants.canManage && (
            <Button iconName="add-plus" onClick={() => setGranting(true)}>
              Grant vendor access
            </Button>
          )
        }
        note={studyLoadFailed ? "Couldn't load the access inherited from the study." : undefined}
        inherited={inherited}
        empty="No one has access to this DTA yet."
      />
      {granting && (
        <GrantVendorAccessModal
          target={dtaTarget}
          dtaName={dtaName}
          sets={grantable}
          granted={dtaGrants.grants}
          onClose={() => setGranting(false)}
        />
      )}
    </>
  )
}

function GrantVendorAccessModal({
  target,
  dtaName,
  sets,
  granted,
  onClose,
}: {
  target: GrantTarget
  dtaName: string
  sets: CapabilitySet[]
  granted: GrantItem[]
  onClose: () => void
}) {
  // Callers without read_vendor on the DTA get a 403 here and type the email instead.
  const { candidates } = useDtaGrantCandidates(target.id)
  // Keep suggesting a person until they hold every set this dialog can grant.
  const suggestions = useMemo(
    () =>
      candidates
        .map((u) => u.email.toLowerCase())
        .filter((email) =>
          sets.some(
            (s) => !granted.some((g) => sameEmail(g.email, email) && g.capability_set === s.name),
          ),
        ),
    [candidates, granted, sets],
  )
  return (
    <GrantAccessModal
      target={target}
      header={`Grant vendor access - ${dtaName}`}
      submitLabel="Grant vendor access"
      sets={sets}
      emailSuggestions={suggestions}
      note="Applies to this DTA only - vendor grants do not cascade."
      onClose={onClose}
    />
  )
}
